/**
 * YMG SDK initialization service for luckyanimal-nawabari.
 *
 * The CDN SDK is loaded in index.html as a <script> tag.
 * This module provides a singleton accessor and a fetch-based fallback helper.
 *
 * Design principles (per .claude/rules):
 *   - No silent fallbacks: errors are surfaced to callers with context.
 *   - baseUrl is explicitly passed (bypass SDK auto-detect corner cases).
 *   - SDK method attempt -> fetch fallback pattern for resilience against
 *     CDN version drift.
 *   - platformId is derived from `utils/platform.getPlatformId()` so native
 *     (Android/iOS) builds report themselves correctly to the Hub.
 *   - Capacitor 時の tokenProvider は session token ではなく、session 作成用の
 *     guest credential を返す。実 API 呼び出しは ymgFetch が Preferences の
 *     session token を Bearer に付与する。
 *
 * Phase 4 (T12) 以降、`getPlatformId()` が認証状態に応じて 'web_user' を返し、
 * `buildGetToken()` が Firebase / LIFF / Capacitor Social Login の ID Token を
 * 返すようになる。
 */
import { getPlatformId, isCapacitorNative, type YmgPlatformId } from '../utils/platform'
import { getAuthState } from './auth'
import { getFirebaseAuth } from './firebase'
import { getLiffIdToken, getSafeLiffRedirectUri, isLocalhostDev } from './liff'
import { AUTH_PREF_KEYS } from '../config/auth'
import { ApiError, NetworkError, notifyReauthRequired } from './ymgAuth'
import { setHubStatus } from './hubStatus'
import { loadSettings } from './settings'

const GAME_ID = import.meta.env.VITE_YMG_GAME_ID || 'luckyanimalnawabari'
const API_BASE_URL = (import.meta.env.VITE_YMG_API_BASE_URL || '').trim()
const SDK_ENV = normalizeSdkEnv(import.meta.env.VITE_YMG_SDK_ENV)
const DEBUG = import.meta.env.VITE_DEBUG_MODE === 'true'
const ENV_PREF_KEY = 'ymg_env'

let sdkInstance: YmgSdk | null = null
let initPromise: Promise<YmgSdk> | null = null
let initFailed = false

/** Returns the configured game ID. */
export function getGameId(): string {
  return GAME_ID
}

/**
 * Get or initialize the YMG SDK singleton.
 * Throws if the CDN script failed to load or initialization fails.
 */
export async function getYmgSdk(): Promise<YmgSdk> {
  if (sdkInstance) return sdkInstance
  if (initFailed) throw new Error('[YmgSDK] initialization previously failed')
  if (initPromise) return initPromise

  initPromise = initializeYmgSdk()
  try {
    sdkInstance = await initPromise
    setHubStatus('online')
    return sdkInstance
  } catch (err) {
    initFailed = true
    setHubStatus('offline')
    throw err
  } finally {
    initPromise = null
  }
}

/**
 * hubProbe から呼ばれる SDK 再 init 試行。
 * `getYmgSdk()` の cooldown / initFailed を強制的に解除して再試行する。
 * 成功時 true（hubStatus は online に遷移）、失敗時 false。
 */
export async function probeYmgSdk(): Promise<boolean> {
  initFailed = false
  if (sdkInstance) return true
  try {
    await getYmgSdk()
    return true
  } catch (err) {
    console.warn('[YmgSDK] probe failed:', err)
    return false
  }
}

/** Check whether the SDK has been successfully initialized. */
export function isSdkReady(): boolean {
  return sdkInstance !== null
}

/** Get the SDK instance without waiting (returns null if not yet initialized). */
export function getSdk(): YmgSdk | null {
  return sdkInstance
}

/**
 * Drop the current SDK singleton after an auth boundary change.
 *
 * `revoke=true` is used for explicit sign-out so the SDK cancels its refresh
 * timer and clears its own session state. Recovery flows use `revoke=false`
 * because a new session has just been persisted and must not be deleted.
 */
export async function resetYmgSdkSession(options: { revoke?: boolean } = {}): Promise<void> {
  const sdk = sdkInstance
  sdkInstance = null
  initPromise = null
  initFailed = false

  if (!options.revoke || !sdk || typeof sdk.logout !== 'function') return

  try {
    await sdk.logout()
  } catch (err) {
    console.warn('[YmgSDK] sdk.logout failed during reset:', err)
  }
}

// ---------------------------------------------------------------------------
// Internal initialization
// ---------------------------------------------------------------------------

function createCapacitorSessionStorage(): NonNullable<YmgSdkInitParams['sessionStorage']> {
  return {
    get: async (key: string) => {
      const { Preferences } = await import('@capacitor/preferences')
      const { value } = await Preferences.get({ key })
      return value ?? null
    },
    set: async (key: string, value: string) => {
      const { Preferences } = await import('@capacitor/preferences')
      await Preferences.set({ key, value })
    },
    remove: async (key: string) => {
      const { Preferences } = await import('@capacitor/preferences')
      await Preferences.remove({ key })
    },
  }
}

async function ensureNativeEnvConsistency(): Promise<void> {
  if (!isCapacitorNative()) return

  const { Preferences } = await import('@capacitor/preferences')
  const currentEnv = (import.meta.env.VITE_APP_ENV || 'unknown').trim() || 'unknown'
  const { value: storedEnv } = await Preferences.get({ key: ENV_PREF_KEY })

  if (storedEnv && storedEnv !== currentEnv) {
    console.warn(`[YmgSDK] Environment changed: ${storedEnv} -> ${currentEnv}; clearing native session`)
    await Preferences.clear()
  }

  await Preferences.set({ key: ENV_PREF_KEY, value: currentEnv })
}

async function clearCapacitorGuestToken(): Promise<void> {
  if (!isCapacitorNative()) return
  const { Preferences } = await import('@capacitor/preferences')
  await Preferences.remove({ key: AUTH_PREF_KEYS.guest })
}

async function removeNativeSessionKeys(): Promise<void> {
  if (!isCapacitorNative()) return
  const { Preferences } = await import('@capacitor/preferences')
  await Promise.all([
    Preferences.remove({ key: AUTH_PREF_KEYS.session }),
    Preferences.remove({ key: AUTH_PREF_KEYS.refresh }),
    Preferences.remove({ key: AUTH_PREF_KEYS.sessionExpiresAt }),
    Preferences.remove({ key: AUTH_PREF_KEYS.refreshExpiresAt }),
    Preferences.remove({ key: AUTH_PREF_KEYS.sessionId }),
  ])
}

async function clearUnrecoverableNativeAuthHint(): Promise<void> {
  if (!isCapacitorNative()) return
  const { Preferences } = await import('@capacitor/preferences')
  const { value: lastStatus } = await Preferences.get({ key: AUTH_PREF_KEYS.lastStatus })
  if (lastStatus !== 'signed_in') return

  const [{ value: refreshToken }, { value: refreshExpiresAtRaw }] = await Promise.all([
    Preferences.get({ key: AUTH_PREF_KEYS.refresh }),
    Preferences.get({ key: AUTH_PREF_KEYS.refreshExpiresAt }),
  ])
  const refreshExpiresAt = refreshExpiresAtRaw ? Number(refreshExpiresAtRaw) : NaN
  const refreshUsable =
    !!refreshToken &&
    Number.isFinite(refreshExpiresAt) &&
    refreshExpiresAt > Date.now()

  if (refreshUsable) return

  console.warn('[YmgSDK] Stored signed-in session is not refreshable; re-authentication required')
  await removeNativeSessionKeys()
  const error = new ApiError(
    401,
    { message: 'Stored session is expired and cannot be refreshed' },
    undefined,
    true,
  )
  notifyReauthRequired(error)
  throw error
}

export async function hasCapacitorSessionToken(): Promise<boolean> {
  if (!isCapacitorNative()) return false
  try {
    const { Preferences } = await import('@capacitor/preferences')
    const { value } = await Preferences.get({ key: AUTH_PREF_KEYS.session })
    return typeof value === 'string' && value.length > 0
  } catch (err) {
    const msg = err instanceof Error ? err.message : 'unknown'
    console.warn(`[YmgSDK] Preferences session read failed: ${msg}`)
    return false
  }
}

async function getOrCreateCapacitorGuestToken(
  platformId: Extract<YmgPlatformId, 'android' | 'ios'>,
): Promise<string> {
  if (!API_BASE_URL) {
    throw new Error('[YmgSDK] VITE_YMG_API_BASE_URL is required for native guest auth')
  }

  const { Preferences } = await import('@capacitor/preferences')
  const { value: cached } = await Preferences.get({ key: AUTH_PREF_KEYS.guest })
  if (cached) return cached

  let deviceId: string | undefined
  try {
    const devicePlugin = (window as unknown as {
      Capacitor?: {
        Plugins?: {
          Device?: {
            getId?: () => Promise<{ identifier?: string }>
          }
        }
      }
    }).Capacitor?.Plugins?.Device
    const result = await devicePlugin?.getId?.()
    deviceId = result?.identifier
  } catch (err) {
    const msg = err instanceof Error ? err.message : 'unknown'
    console.warn(`[YmgSDK] Device.getId() failed, issuing guest without deviceId: ${msg}`)
  }

  const response = await fetch(new URL('/auth/guest', API_BASE_URL).toString(), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      platformId,
      ...(deviceId ? { deviceId } : {}),
    }),
  })

  if (!response.ok) {
    throw new Error(`[YmgSDK] Guest auth failed: ${response.status}`)
  }

  const data = (await response.json()) as { token?: string }
  if (!data?.token) {
    throw new Error('[YmgSDK] Guest auth returned no token')
  }

  await Preferences.set({ key: AUTH_PREF_KEYS.guest, value: data.token })
  return data.token
}

async function resolveNativeSdkCredential(
  platformId: Extract<YmgPlatformId, 'android' | 'ios'>,
): Promise<string> {
  return getOrCreateCapacitorGuestToken(platformId)
}

async function initializeYmgSdk(retried = false): Promise<YmgSdk> {
  if (typeof YmgSDK === 'undefined') {
    throw new Error('[YmgSDK] SDK not loaded from CDN')
  }

  const platformId = getPlatformId()

  if (isCapacitorNative()) {
    const nativePlatformId = platformId as Extract<YmgPlatformId, 'android' | 'ios'>
    await ensureNativeEnvConsistency()
    await clearUnrecoverableNativeAuthHint()

    try {
      const initParams: YmgSdkInitParams = {
        gameId: GAME_ID,
        env: SDK_ENV,
        locale: getSdkLocale(),
        platformId: nativePlatformId,
        tokenProvider: async () => resolveNativeSdkCredential(nativePlatformId),
        useSessionToken: true,
        sessionStorage: createCapacitorSessionStorage(),
        onSessionExpired: async () => {
          const error = new ApiError(
            401,
            { message: 'SDK session expired and cannot be refreshed' },
            undefined,
            true,
          )
          notifyReauthRequired(error)
        },
        onEvent: handleSdkEvent,
        ui: createNoopSdkUi(),
      }
      if (API_BASE_URL) {
        initParams.baseUrl = API_BASE_URL
      }

      if (DEBUG) {
        console.log('[YmgSDK] Initializing native SDK:', {
          gameId: GAME_ID,
          platformId: nativePlatformId,
          baseUrl: initParams.baseUrl || '(auto-detect)',
        })
      }

      const sdk = await YmgSDK.init(initParams)
      console.log('[YmgSDK] Native SDK initialized successfully')
      return sdk
    } catch (err) {
      if (!retried) {
        const msg = err instanceof Error ? err.message : 'unknown'
        console.warn(`[YmgSDK] Native init failed, clearing guest token and retrying once: ${msg}`)
        await clearCapacitorGuestToken()
        return initializeYmgSdk(true)
      }
      throw err
    }
  }

  // Web 経由（LIFF）では LINE 認証を必須とする設計（Match3 相当）。
  // Capacitor ネイティブは LIFF を使わず Preferences の session token を再利用するため除外。
  // localhost + VITE_DEV_AUTH_BYPASS=true は開発用にゲスト継続を許可する。
  // 上記いずれにも該当しないのに LIFF ID Token が取得できない場合は、
  // web_guest への無言フォールバックを防ぐため明示的に失敗させる。
  const liffRequired = !isCapacitorNative() && !isLocalDevBypass()
  if (liffRequired && !hasLiffToken()) {
    throw new Error('Authentication required: LIFF token not available')
  }

  const initParams: YmgSdkInitParams = {
    gameId: GAME_ID,
    env: SDK_ENV,
    locale: getSdkLocale(),
    // Phase 1-2 では認証未実装のため、web は常に 'web_guest'。
    // Phase 4 (T12) 以降は `getPlatformId()` がログイン状態に応じて
    // 'web_user' を返すようになり、このまま引き継がれる。
    platformId,
    // CDN v1.x の UnifiedSdkOptions は `tokenProvider` のみを受け付ける。
    // 過去 `getToken` という名前で渡していたため LIFF ID Token が全く送信されず、
    // bootstrap が匿名 sdkToken を発行して /profiles/me が 401 になる回帰を踏んだ。
    tokenProvider: buildGetToken(platformId),
    onAuthRequired: buildOnAuthRequired(platformId),
    onEvent: handleSdkEvent,
    ui: createNoopSdkUi(),
  }

  // baseUrl を明示指定（v0.3.0 で baseUrl 省略による Invalid URL バグ経験あり）
  if (API_BASE_URL) {
    initParams.baseUrl = API_BASE_URL
  }

  if (DEBUG) {
    console.log('[YmgSDK] Initializing:', {
      gameId: GAME_ID,
      platformId,
      baseUrl: initParams.baseUrl || '(auto-detect)',
    })
  }

  const sdk = await YmgSDK.init(initParams)
  console.log('[YmgSDK] Initialized successfully')
  return sdk
}

function normalizeSdkEnv(value: unknown): 'sandbox' | 'production' {
  return value === 'sandbox' ? 'sandbox' : 'production'
}

function getSdkLocale(): string {
  const locale = loadSettings().locale
  switch (locale) {
    case 'ja':
      return 'ja_JP'
    case 'es':
      return 'es_ES'
    case 'pt-BR':
      return 'pt_BR'
    case 'fr':
      return 'fr_FR'
    case 'ar':
      return 'ar_SA'
    case 'id':
      return 'id_ID'
    case 'vi':
      return 'vi_VN'
    case 'th':
      return 'th_TH'
    case 'de':
      return 'de_DE'
    default:
      return 'en_US'
  }
}

function createNoopSdkUi(): NonNullable<YmgSdkInitParams['ui']> {
  return {
    showLoading: () => {},
    showRetry: () => {},
    showError: () => {},
    showInfo: () => {},
    showLoginRetry: async () => 'cancel',
  }
}

/**
 * 認証クレデンシャル（ID Token 等）の取得方法をプラットフォームごとに返す。
 *
 * 取得優先度（高→低）:
 *   1. LINE Mini App (LIFF): `getLiffIdToken()` の返り値
 *      — line_mini では LIFF ID Token を Hub に送る必要があるため最優先。
 *      — isLiffReady() 内部で LINE Client + ログイン済みを保証。
 *   2. 認証済み（Google 等）: auth.getAuthState() の idToken
 *   3. それ以外: null → SDK が内部でゲストトークンを発行
 *
 * Capacitor ネイティブは initializeYmgSdk() の分岐で sessionStorage + guest
 * credential を渡すため、この tokenProvider には入らない。
 */
function buildGetToken(platformId: YmgPlatformId): () => Promise<string | null> {
  return async () => {
    // LINE Mini App (LIFF) が利用可能なら LIFF ID Token を最優先で返す
    const liffToken = getLiffIdToken()
    if (liffToken) return liffToken

    // 認証済みなら ID Token を優先
    const state = getAuthState()
    if (state.status === 'signed_in' && state.idToken) {
      return state.idToken
    }

    // line_mini として初期化している途中で LIFF トークンが取得できない場合は
    // ゲストで動かすと別ユーザー扱いになり、認証済みデータが失われる。
    // 開発用の localhost バイパスを除いて明示的に失敗させる。
    if (platformId === 'line_mini' && !isLocalDevBypass()) {
      throw new Error('LIFF ID token missing; cannot authenticate as line_mini')
    }

    return null
  }
}

/**
 * localhost + VITE_DEV_AUTH_BYPASS=true のときのみ、LIFF トークンなしでも
 * ゲスト認証を許可するかを返す。それ以外では常に false。
 */
export function isLocalDevBypass(): boolean {
  return isLocalhostDev() && import.meta.env.VITE_DEV_AUTH_BYPASS === 'true'
}

/**
 * buildGetToken() と同じ情報源（getLiffIdToken()）で LIFF ID Token が取得できるかを
 * 同期的に確認する。SDK 初期化前のゲート判定で使う。
 */
function hasLiffToken(): boolean {
  const t = getLiffIdToken()
  return typeof t === 'string' && t.length > 0
}

/**
 * SDK が 401 を検出したときに呼ばれる再認証コールバック。
 *
 * 挙動:
 *   - LINE Client 外 + line_mini: `liff.login()` で再ログインへリダイレクト
 *     （redirectUri は Open Redirect 回避のため origin + pathname のみ）。
 *   - 上記以外（LINE Client 内 / Capacitor / localhost / 認証済みブラウザ）:
 *     `notifyReauthRequired` で UI に 401 を通知して再ログイン画面に倒す。
 *     以前は `console.log` のみで通知が握り潰されていた（Blue/Codex 指摘）。
 */
function buildOnAuthRequired(platformId: YmgPlatformId): () => Promise<void> {
  return async () => {
    const w = window as unknown as {
      liff?: {
        isInClient?: () => boolean
        login?: (o?: { redirectUri?: string }) => void
      }
    }
    const isInClient = w.liff?.isInClient?.() ?? false

    if (!isInClient && platformId === 'line_mini') {
      console.log('[YmgSDK] onAuthRequired: redirecting to LINE login (external browser)')
      try {
        w.liff?.login?.({ redirectUri: getSafeLiffRedirectUri() })
      } catch (e) {
        // fallback:ok — liff.login が失敗した場合も UI 側の再ログイン導線に倒す。
        const msg = e instanceof Error ? e.message : 'unknown'
        console.warn(`[YmgSDK] liff.login threw: ${msg}`)
      }
    }

    // いずれのケースでも再認証イベントは UI に通知する。
    // 外部ブラウザは上でリダイレクトを先に走らせているが、これは同期 API ではない
    // 可能性があるため、notifyReauthRequired を並走させて UI の一貫性を担保する。
    const error = new ApiError(
      401,
      { message: 'SDK reported re-authentication required' },
      undefined,
      true,
    )
    notifyReauthRequired(error)
  }
}

// ---------------------------------------------------------------------------
// Fallback fetch helper
// ---------------------------------------------------------------------------

/**
 * Low-level fetch wrapper for direct YMG API access.
 * Used as a fallback when an SDK method is missing at runtime
 * (e.g. type definitions are ahead of the CDN version).
 *
 * エラーモデル（Sweeper `ymgFetchJson` 準拠 — fail-closed）:
 *   - HTTP エラーは {@link ApiError} で throw（status / body / requiresReauth 付き）
 *   - ネットワーク例外（fetch() 自体が throw）は {@link NetworkError} に包んで throw
 *   - 401 on line_mini: 500ms 待機して 1 回だけリトライ（LIFF が裏で ID Token を
 *     リフレッシュしている想定）
 *   - 401 on Capacitor (android/ios): Preferences を再読し、SDK が裏で更新した
 *     セッショントークンがあれば新トークンで 1 回リトライ（Sweeper
 *     `ymgFetchJsonCapacitor` 相当）
 *   - 401 after retry: `notifyReauthRequired` で再認証 UI に通知し、外部ブラウザなら
 *     `liff.login()` で LINE ログインにリダイレクト、requiresReauth=true で throw
 *   - 403 / 404: non-retryable な恒久エラーとして即 throw
 *   - その他 (4xx/5xx): requiresReauth=false の ApiError で throw
 *
 * Authorization ヘッダの付与方針:
 *   - auth.getAuthState() が signed_in なら `Bearer <idToken>`
 *   - Capacitor ネイティブで Preferences に session token があればそれを Bearer
 *   - それ以外は付けない（SDK が bootstrap でゲストトークンを取得する設計）
 *
 * web_guest の公開読み取り API（/avatar-icons 等）は Authorization なしでも動くため、
 * この fetch helper は「認証情報があれば付ける。無ければ付けずに投げる」設計。
 */
export async function ymgFetch<T>(
  path: string,
  init?: RequestInit & { needsGameIdHeader?: boolean; skipAuth?: boolean },
  attempt: number = 0,
): Promise<T> {
  if (!API_BASE_URL) {
    // デフォルトURLを埋め込まないことで、環境変数未設定のまま本番 URL へ誤配信するリスクを排除する。
    throw new Error(
      '[ymgFetch] VITE_YMG_API_BASE_URL is not configured. Check .env.<mode>.',
    )
  }
  const url = new URL(path, API_BASE_URL).toString()
  const platformId = getPlatformId()

  const headers = new Headers(init?.headers)
  headers.set('X-Platform-Id', platformId)
  if (init?.needsGameIdHeader) {
    headers.set('X-Game-Id', GAME_ID)
  }

  // 認証ヘッダ付与（skipAuth=true なら省略）。Bearer に使ったトークンは
  // Capacitor の 401 リトライ判定で「SDK が裏で更新したか」を比較するために記憶。
  let bearerUsed: string | null = null
  if (!init?.skipAuth && !headers.has('Authorization')) {
    const token = await resolveBearerToken()
    if (token) {
      headers.set('Authorization', `Bearer ${token}`)
      bearerUsed = token
    }
  }

  const { needsGameIdHeader: _omitted, skipAuth: _skipAuth, ...fetchInit } = init ?? {}
  void _omitted
  void _skipAuth

  // fetch() 自体の例外（オフライン・DNS 失敗・CORS 等）は NetworkError に正規化する。
  // ApiError は HTTP レスポンスを得られたケース専用。
  let res: Response
  try {
    res = await fetch(url, { ...fetchInit, headers })
  } catch (err) {
    const origin = err instanceof Error ? err : new Error(String(err))
    setHubStatus('offline')
    throw new NetworkError(
      `ymgFetch network error: ${origin.message}`,
      'NETWORK_FAILURE',
      { originalError: origin },
    )
  }

  // 5xx は Hub 障害として扱う。401/403 はサーバー到達できているので status を変えない
  // （状態フリッカー防止）。OfflineBanner は hubStatus のみを見る。
  if (res.status >= 500) {
    setHubStatus('offline')
  } else if (res.status !== 401 && res.status !== 403) {
    setHubStatus('online')
  }

  if (res.ok) {
    const rawText = await res.text()
    if (!rawText.trim()) return {} as T
    return JSON.parse(rawText) as T
  }

  // fallback:ok — エラーレスポンスが非 JSON（HTML エラーページ・空ボディ等）の
  // ケースに備えて空オブジェクトに倒す。ApiError は status/body の両方を保持
  // するため、body が空でも呼び出し元は status で十分判断できる。
  const body = (await res.json().catch(() => ({}))) as Record<string, unknown>

  // 401 リトライはプラットフォームごとに判定条件が異なる。
  //   - line_mini: LIFF が裏で ID Token をリフレッシュしている想定で一律 500ms 待機。
  //   - Capacitor (android/ios): SDK がセッショントークンを更新していれば新トークン
  //     でリトライする。Preferences を再読して Bearer に使った値と比較する。
  if (res.status === 401 && attempt < 1 && !init?.skipAuth) {
    if (platformId === 'line_mini') {
      console.warn('[ymgFetch] 401 on line_mini — retrying once after 500ms')
      await new Promise((resolve) => setTimeout(resolve, 500))
      return ymgFetch<T>(path, init, attempt + 1)
    }

    if (isCapacitorNative()) {
      try {
        await refreshNativeSessionViaSdk()
        const { Preferences } = await import('@capacitor/preferences')
        const { value: freshToken } = await Preferences.get({ key: AUTH_PREF_KEYS.session })
        if (freshToken && freshToken !== bearerUsed) {
          console.warn('[ymgFetch] 401 on Capacitor — session token was refreshed, retrying')
          return ymgFetch<T>(path, init, attempt + 1)
        }
      } catch (err) {
        // fallback:ok — Preferences 再読みに失敗しても、以下の通常 401 フローで再認証 UI に倒す。
        const msg = err instanceof Error ? err.message : 'unknown'
        console.warn(`[ymgFetch] Capacitor Preferences re-read failed: ${msg}`)
      }
    }
  }

  // 403 / 404: non-retryable
  if (res.status === 403 || res.status === 404) {
    throw new ApiError(res.status, body, undefined, false)
  }

  // 401 after retry: 再認証が必要
  if (res.status === 401) {
    console.error('[ymgFetch] 401 after retry — re-authentication required')

    // 外部ブラウザ（LINE Client 外）で line_mini を使っている場合は、
    // `liff.login()` で LINE ログインへ誘導する。これは一方向遷移なので
    // 続けて notifyReauthRequired + throw も行い、UI が止まらないようにする。
    const w = window as unknown as {
      liff?: {
        isInClient?: () => boolean
        login?: (o?: { redirectUri?: string }) => void
      }
    }
    const isInClient = w.liff?.isInClient?.() ?? false
    if (!isInClient && platformId === 'line_mini') {
      try {
        // redirectUri は origin + pathname のみに絞る（Open Redirect 対策）。
        w.liff?.login?.({ redirectUri: getSafeLiffRedirectUri() })
      } catch (e) {
        // fallback:ok — LIFF login 呼び出し失敗は致命的ではない。UI 側の再ログイン導線に倒す。
        const msg = e instanceof Error ? e.message : 'unknown'
        console.warn(`[ymgFetch] liff.login() threw: ${msg}`)
      }
    }

    const error = new ApiError(res.status, body, undefined, true)
    notifyReauthRequired(error)
    throw error
  }

  throw new ApiError(res.status, body, undefined, false)
}

async function refreshNativeSessionViaSdk(): Promise<void> {
  if (!isCapacitorNative()) return
  if (!API_BASE_URL) return
  try {
    const { Preferences } = await import('@capacitor/preferences')
    const { value: refreshToken } = await Preferences.get({ key: AUTH_PREF_KEYS.refresh })
    if (!refreshToken) return

    const res = await fetch(new URL('/auth/session/refresh', API_BASE_URL).toString(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Game-Id': GAME_ID,
        'X-Platform-Id': getPlatformId(),
      },
      body: JSON.stringify({ refreshToken }),
    })
    if (!res.ok) return

    const body = (await res.json().catch(() => ({}))) as {
      sessionId?: string
      sessionToken?: string
      refreshToken?: string
      expiresAt?: number
      refreshExpiresAt?: number
    }
    if (
      !body.sessionToken ||
      !body.refreshToken ||
      typeof body.expiresAt !== 'number' ||
      typeof body.refreshExpiresAt !== 'number'
    ) {
      return
    }

    await Promise.all([
      Preferences.set({ key: AUTH_PREF_KEYS.session, value: body.sessionToken }),
      Preferences.set({ key: AUTH_PREF_KEYS.refresh, value: body.refreshToken }),
      Preferences.set({ key: AUTH_PREF_KEYS.sessionExpiresAt, value: String(body.expiresAt) }),
      Preferences.set({ key: AUTH_PREF_KEYS.refreshExpiresAt, value: String(body.refreshExpiresAt) }),
      Preferences.set({ key: AUTH_PREF_KEYS.sessionId, value: body.sessionId ?? '' }),
    ])

    // SDK private session state is not externally mutable. Force the next SDK call to restore
    // the just-refreshed session from Capacitor Preferences.
    sdkInstance = null
    initPromise = null
    initFailed = false
  } catch (err) {
    const msg = err instanceof Error ? err.message : 'unknown'
    console.warn(`[ymgFetch] SDK-driven native session refresh failed: ${msg}`)
  }
}

/**
 * 現在の状態に応じた Bearer token（あれば）を返す。
 * 優先度: LIFF ID Token > Web Firebase ID Token > Capacitor session > auth state idToken > null
 */
async function resolveBearerToken(): Promise<string | null> {
  const liffToken = getLiffIdToken()
  if (liffToken) return liffToken

  if (!isCapacitorNative()) {
    const state = getAuthState()
    if (state.status === 'signed_in' && state.provider === 'google') {
      try {
        const currentUser = getFirebaseAuth()?.currentUser
        const firebaseToken = await currentUser?.getIdToken()
        if (firebaseToken) return firebaseToken
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'unknown'
        console.warn(`[ymgFetch] Firebase ID token refresh failed: ${msg}`)
      }
    }
  }

  if (isCapacitorNative()) {
    try {
      const { Preferences } = await import('@capacitor/preferences')
      const { value } = await Preferences.get({ key: AUTH_PREF_KEYS.session })
      if (value) return value
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'unknown'
      console.warn(`[ymgFetch] Preferences read failed: ${msg}`)
    }
  }

  const state = getAuthState()
  if (state.status === 'signed_in' && state.idToken) return state.idToken
  return null
}

// ---------------------------------------------------------------------------
// SDK error helpers
// ---------------------------------------------------------------------------

/**
 * CDN SDK (`sdk.api.*`) の呼び出しから投げられたエラーが 401 認証エラーかを判定する。
 * CDN SDK は `YmgApiError` で status プロパティを持つが型は公開されていないため、
 * 構造的に status === 401 だけをチェックする。
 */
export function isSdkAuthError(error: unknown): boolean {
  if (error instanceof ApiError) return error.status === 401
  if (!error || typeof error !== 'object') return false
  const status = (error as { status?: number }).status
  return status === 401
}

// ---------------------------------------------------------------------------
// SDK event handler
// ---------------------------------------------------------------------------

function handleSdkEvent(event: YmgSdkEvent): void {
  switch (event.type) {
    case 'bootstrap:success':
      if (DEBUG) console.log('[YmgSDK] Bootstrap OK')
      break
    case 'bootstrap:failed':
      console.error(`[YmgSDK] Bootstrap FAILED: ${String(event['message'] ?? '')}`)
      // SDK init() は成功したが bootstrap が後段で失敗した場合、シングルトンが
      // 「使えない壊れた SDK」を保持し続けるのを避けるため null 化する。
      // 次回 getYmgSdk() で fresh init を試みられるよう initFailed も解除する。
      // Sweeper `ymgSdk.ts` の `sdkPromise = null` と同等の防御。
      sdkInstance = null
      initFailed = false
      void clearCapacitorGuestToken().catch((err) => {
        const msg = err instanceof Error ? err.message : 'unknown'
        console.warn(`[YmgSDK] Failed to clear cached guest token: ${msg}`)
      })
      break
    case 'session:created':
    case 'session:refreshed':
    case 'session:restored':
      if (DEBUG) console.log(`[YmgSDK] Session event: ${event.type}`)
      break
    case 'session:expired':
    case 'session:revoked':
      notifyReauthRequired(
        new ApiError(401, { message: `Session ${String(event.type).split(':')[1]}` }, undefined, true),
      )
      break
    case 'request:error':
      if (DEBUG) console.warn(`[YmgSDK] Request error: ${String(event['message'] ?? '')}`)
      break
    default:
      if (DEBUG) console.log(`[YmgSDK] Event: ${event.type}`)
  }
}
